import { useCallback } from 'react';
import { 
    trackButtonClick, 
    trackFormSubmit, 
    trackSearch, 
    trackFilter, 
    trackDownload, 
    trackError 
} from '../services/auditService';

/**
 * Custom hook for audit tracking
 * Provides convenient methods for tracking user activities
 */
export const useAuditTracking = () => {
    // Track button clicks with optional context
    const trackClick = useCallback((buttonLabel, context = {}) => {
        trackButtonClick(buttonLabel, {
            timestamp: Date.now(),
            ...context
        });
    }, []);

    // Track form submissions
    const trackForm = useCallback((formType, formData = {}, isSuccess = true) => {
        trackFormSubmit(formType, formData, isSuccess);
    }, []);

    // Track search actions
    const trackSearchAction = useCallback((searchTerm, resultCount = 0, context = {}) => {
        trackSearch(searchTerm, resultCount);
        
        // Also track as filter if search criteria provided
        if (context.filters) {
            trackFilter({ search: searchTerm, ...context.filters });
        }
    }, []);

    // Track filter actions
    const trackFilterAction = useCallback((filterCriteria) => {
        trackFilter(filterCriteria);
    }, []);

    // Track file downloads
    const trackDownloadAction = useCallback((fileName, fileType, context = {}) => {
        trackDownload(fileName, fileType);
    }, []);

    // Track errors with context
    const trackErrorAction = useCallback((error, context = {}) => {
        trackError(error, context);
    }, []);

    // Track navigation/page changes
    const trackNavigation = useCallback((fromPage, toPage, context = {}) => {
        trackClick('navigation', {
            fromPage,
            toPage,
            navigationType: context.type || 'click',
            ...context
        });
    }, []);

    // Track CRUD operations
    const trackCRUD = useCallback((operation, resource, resourceId = null, context = {}) => {
        const operationMap = {
            create: 'Create',
            read: 'View',
            update: 'Edit',
            delete: 'Delete'
        };
        
        trackClick(`${operationMap[operation]} ${resource}`, {
            operation,
            resource,
            resourceId,
            ...context
        });
    }, []);

    // Track modal/dialog actions
    const trackModal = useCallback((action, modalName, context = {}) => {
        trackClick(`${action} ${modalName}`, {
            modalAction: action,
            modalName,
            ...context
        });
    }, []);

    // Track tab changes
    const trackTabChange = useCallback((tabName, tabIndex, context = {}) => {
        trackClick('Tab Change', {
            tabName,
            tabIndex,
            ...context
        });
    }, []);

    // Track bulk actions
    const trackBulkAction = useCallback((action, itemCount, resource, context = {}) => {
        trackClick(`Bulk ${action}`, {
            action,
            itemCount,
            resource,
            ...context
        });
    }, []);

    return {
        // Basic tracking
        trackClick,
        trackForm,
        trackSearchAction,
        trackFilterAction,
        trackDownloadAction,
        trackErrorAction,
        
        // Specific use cases
        trackNavigation,
        trackCRUD,
        trackModal,
        trackTabChange,
        trackBulkAction
    };
};

export default useAuditTracking; 